export type AnalyticsConsent = 'granted' | 'denied'
export type AnalyticsConsentSnapshot = AnalyticsConsent | null | 'loading'

export const ANALYTICS_CONSENT_STORAGE_KEY = 'mp_optional_analytics_consent_v2'
export const LEGACY_POSTHOG_CONSENT_STORAGE_KEY = 'mp_posthog_consent_v1'
export const ANALYTICS_CONSENT_CHANGED_EVENT = 'microplastics:analytics-consent-changed'
const ANALYTICS_PREFERENCES_VISIBILITY_EVENT =
  'microplastics:analytics-preferences-visibility'

interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function readAnalyticsConsentUnchecked(storage: StorageLike): AnalyticsConsent | null {
  const value = storage.getItem(ANALYTICS_CONSENT_STORAGE_KEY)
  if (value === 'granted' || value === 'denied') return value

  // The v1 choice covered PostHog only. Its denial safely carries forward,
  // but its grant cannot be broadened to GA and Vercel without asking again.
  return storage.getItem(LEGACY_POSTHOG_CONSENT_STORAGE_KEY) === 'denied'
    ? 'denied'
    : null
}

let volatileConsent: AnalyticsConsent | null = null
let preferencesVisible = false

export function readAnalyticsConsent(storage?: StorageLike): AnalyticsConsent | null {
  if (!storage) return null
  try {
    return readAnalyticsConsentUnchecked(storage)
  } catch {
    return null
  }
}

/**
 * Prefer an accessible browser store exactly, including an explicit clear.
 * The volatile value is only a fallback when storage access itself throws.
 */
export function readAnalyticsConsentWithFallback(
  storage: StorageLike,
  fallback: AnalyticsConsent | null
): AnalyticsConsent | null {
  try {
    return readAnalyticsConsentUnchecked(storage)
  } catch {
    return fallback
  }
}

export function writeAnalyticsConsent(
  consent: AnalyticsConsent,
  storage?: StorageLike
): void {
  storage?.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent)
}

export function getBrowserAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === 'undefined') return null
  try {
    // Accessing the localStorage property itself can throw in opaque,
    // sandboxed, or policy-blocked browser contexts. Keep acquisition and the
    // subsequent reads inside the same boundary so analytics stays fail-closed.
    return readAnalyticsConsentWithFallback(window.localStorage, volatileConsent)
  } catch {
    return volatileConsent
  }
}

export function setBrowserAnalyticsConsent(consent: AnalyticsConsent): void {
  if (typeof window === 'undefined') return

  volatileConsent = consent
  try {
    writeAnalyticsConsent(consent, window.localStorage)
  } catch {
    // Storage can be unavailable in hardened/private browsing. The explicit
    // choice still applies for this page through the in-memory fallback.
  }
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT))
}

export function subscribeToAnalyticsConsent(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const onStorage = (event: StorageEvent) => {
    if (isAnalyticsConsentStorageEvent(event.key)) {
      onChange()
    }
  }
  window.addEventListener('storage', onStorage)
  window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, onChange)

  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, onChange)
  }
}

/** `key === null` is the browser signal for `localStorage.clear()`. */
export function isAnalyticsConsentStorageEvent(key: string | null): boolean {
  return key === null ||
    key === ANALYTICS_CONSENT_STORAGE_KEY ||
    key === LEGACY_POSTHOG_CONSENT_STORAGE_KEY
}

/**
 * Providers may run only for an affirmative grant. Removing or clearing the
 * stored choice therefore maps to denial for an already-loaded runtime.
 */
export function providerConsentForSnapshot(
  snapshot: AnalyticsConsentSnapshot
): AnalyticsConsent | null {
  if (snapshot === 'loading') return null
  return snapshot === 'granted' ? 'granted' : 'denied'
}

export function getAnalyticsPreferencesVisible(): boolean {
  return preferencesVisible
}

export function setAnalyticsPreferencesVisible(visible: boolean): void {
  preferencesVisible = visible
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(ANALYTICS_PREFERENCES_VISIBILITY_EVENT))
}

export function subscribeToAnalyticsPreferencesVisibility(
  onChange: () => void
): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(ANALYTICS_PREFERENCES_VISIBILITY_EVENT, onChange)
  return () => window.removeEventListener(ANALYTICS_PREFERENCES_VISIBILITY_EVENT, onChange)
}
